import { chain, sumBy } from 'lodash';

import { SUPPORTED_PORTAL_CURRENCIES } from 'shared/constants';
import { RefreshShoppingCartResponse, ShoppingCartItemPrice } from 'shared/mutations';
import { GetShoppingCartResponse } from 'shared/queries';
import { ShoppingCartItem, ShoppingCartItemsGroup, User } from 'shared/types';
import { TypedIntlShape } from '../locale/messages';

export const ALLOWED_PARTS_OF_CONVERTER = [0, 0.25, 0.5, 0.75, 1];

export const calculateTotalPrice = (item: ShoppingCartItem): number =>
  item.unitsCount * item.partOfConverter * item.unitPrice;

export const calculateGroupTotalPrice = (group: ShoppingCartItemsGroup): number =>
  group.unitsCount * group.partOfConverter * group.avgPrice;

interface CartSummary {
  itemsInCart: number;
  totalConverters: number;
  totalPrice: number;
  averagePrice: number;
}

export const getCartSummary = (
  items: ShoppingCartItem[],
  groups: ShoppingCartItemsGroup[],
): CartSummary => {
  const totalItemsConverters = items.reduce(
    (acc, item) => acc + item.unitsCount * item.partOfConverter,
    0,
  );

  const totalGroupConverters = groups.reduce(
    (acc, group) => acc + group.unitsCount * group.partOfConverter,
    0,
  );

  const totalConverters = totalItemsConverters + totalGroupConverters;

  const totalItemPrice = items.reduce((acc, item) => acc + calculateTotalPrice(item), 0);
  const totalGroupPrice = groups.reduce((acc, group) => acc + calculateGroupTotalPrice(group), 0);
  const totalPrice = totalItemPrice + totalGroupPrice;

  return {
    itemsInCart: sumBy(items, 'unitsCount') + sumBy(groups, 'unitsCount'),
    totalConverters,
    totalPrice,
    averagePrice: totalConverters === 0 ? 0 : totalPrice / totalConverters,
  };
};

export const formatPartOfConverter = (
  value: number,
  intl: TypedIntlShape,
  removeEntities?: boolean,
): string => {
  let fullText = intl.formatMessage({ id: 'ShoppingCart.PartOfConverter.Full' });

  if (removeEntities) {
    fullText = fullText.replace(/&[^;]+;/g, '');
  }

  const map = new Map([
    [0, intl.formatMessage({ id: 'ShoppingCart.PartOfConverter.Empty' })],
    [0.25, '1/4'],
    [0.5, '1/2'],
    [0.75, '3/4'],
    [1, fullText],
  ]);
  return map.get(value) ?? `${value}`;
};

export const isUsingHedgePricing = (user: User): boolean =>
  chain(user.prices)
    .pick(['ptPriceSource', 'pdPriceSource', 'rhPriceSource'])
    .values()
    .includes('HEDGE')
    .value();

const CURRENCY_MAP: Record<SUPPORTED_PORTAL_CURRENCIES, keyof ShoppingCartItemPrice> = {
  [SUPPORTED_PORTAL_CURRENCIES.USD]: 'usdPerUnit',
  [SUPPORTED_PORTAL_CURRENCIES.EUR]: 'eurPerUnit',
  [SUPPORTED_PORTAL_CURRENCIES.CAD]: 'cadPerUnit',
  [SUPPORTED_PORTAL_CURRENCIES.MXN]: 'mxnPerUnit',
  [SUPPORTED_PORTAL_CURRENCIES.PLN]: 'plnPerUnit',
};

export const getPriceForCurrency = (
  priceObj: ShoppingCartItemPrice,
  currency?: SUPPORTED_PORTAL_CURRENCIES,
): number | undefined => {
  if (!currency) return undefined;
  return priceObj[CURRENCY_MAP[currency]];
};

export const calculateAvgPrice = (
  items: Array<{ price: ShoppingCartItemPrice }>,
  currency?: SUPPORTED_PORTAL_CURRENCIES,
): number => {
  if (!items.length) return 0;
  const prices = items
    .map(item => getPriceForCurrency(item.price, currency))
    .filter((price): price is number => price !== undefined);
  if (!prices.length) return 0;
  const total = prices.reduce((sum, price) => sum + price, 0);
  return total / prices.length;
};

/**
 * Updates the cached shopping cart data with new prices from the refresh response.
 * Note: The refresh endpoint is authoritative — items not in the response were
 * soft-deleted server-side (e.g., due to price calculation failures) and should
 * be removed from cache.
 */
export const updateCachedPrices = (
  cachedData: GetShoppingCartResponse,
  data: RefreshShoppingCartResponse,
  currency?: SUPPORTED_PORTAL_CURRENCIES,
): GetShoppingCartResponse => {
  const itemPricesById = new Map(data.shoppingCartItems.map(item => [item.id, item.price]));
  const groupsById = new Map(data.shoppingCartItemsGroups.map(group => [group.id, group]));

  return {
    shoppingCartItems: cachedData.shoppingCartItems
      .filter(item => itemPricesById.has(item.id))
      .map(item => {
        const updatedPrice = itemPricesById.get(item.id)!;
        const newPrice = getPriceForCurrency(updatedPrice, currency);
        // Preserve existing price if currency is undefined or price is not available
        return { ...item, unitPrice: newPrice ?? item.unitPrice };
      }),
    shoppingCartItemsGroups: cachedData.shoppingCartItemsGroups
      .filter(group => groupsById.has(group.id))
      .map(group => {
        const updatedGroup = groupsById.get(group.id)!;
        const groupItemPricesById = new Map(
          updatedGroup.shoppingCartItems.map(item => [item.id, item.price]),
        );

        return {
          ...group,
          avgPrice: calculateAvgPrice(updatedGroup.shoppingCartItems, currency) || group.avgPrice,
          shoppingCartItems: group.shoppingCartItems
            .filter(item => groupItemPricesById.has(item.id))
            .map(item => {
              const updatedPrice = groupItemPricesById.get(item.id)!;
              const newPrice = getPriceForCurrency(updatedPrice, currency);
              // Preserve existing price if currency is undefined or price is not available
              return { ...item, unitPrice: newPrice ?? item.unitPrice };
            }),
        };
      }),
  };
};

export const countTotalItemsInCart = (data: GetShoppingCartResponse) =>
  data.shoppingCartItems.length +
  data.shoppingCartItemsGroups.reduce((sum, group) => sum + group.shoppingCartItems.length, 0);
